import React from 'react';
import PropTypes from 'prop-types';

import cx from 'classnames';

import withTheme from 'core/hocs/withTheme';

import themePropTypes from 'core/utils/prop-types/theme';
import { sizeTypes } from 'core/utils/prop-types/common';

import isNotEmptyReactElementContent from 'core/utils/content';

import styles from './index.styl';

const sizeContract = `
  {
    font,
    width,
    height,
    padding,
  }
`;

const stateContract = `
  {
    border,
    background,
    placeholderColor,
  }
`;

const typeContract = `
  {
    idle: ${stateContract},
    hover: ${stateContract},
    focused: ${stateContract},
    disabled: ${stateContract},
    error: ${stateContract},
  }
`;

/**
 * @author Pavel Povalyaev (<p.povalyaev@rambler-co.ru>)
 */

function Input(props) {
  const {
    children,
    error,
    innerRef,
    noIconsPadding,
    radius,
    sheet, // eslint-disable-line
    size,
    disabled,
    height,
    theme: {
      animations: {
        hover: hoverAnimation,
      },
      controls: {
        input: inputAtoms,
        input: {
          const: {
            sizes: {
              [size]: inputSizeAtoms,
            },
          },
          basic: inputStateAtoms,
        },
      },
    },
    className,
    inputClassName,
    iconClassName,
    ...inputProps
  } = props;

  return (
    <label className={cx('label', styles.label, className)}>
      <style jsx>{`
        .label
          height ${height || inputSizeAtoms.height}
        .borderer
          background ${inputStateAtoms[disabled ? 'disabled' : 'idle'].background}
          border ${inputStateAtoms[disabled ? 'disabled' : 'idle'].border}
          border-radius ${radius || inputAtoms.const.radius}
          transition ${'background ' + hoverAnimation + ',border ' + hoverAnimation}
          .input:hover ~ &
            background ${inputStateAtoms[disabled ? 'disabled' : 'hover'].background}
            border ${inputStateAtoms[disabled ? 'disabled' : 'hover'].border}
          .input:focus ~ &
            background ${inputStateAtoms[disabled ? 'disabled' : 'focused'].background}
            border ${inputStateAtoms[disabled ? 'disabled' : 'focused'].border}
          &:disabled
            background ${inputAtoms.basic.disabled.background}
            border ${inputAtoms.basic.disabled.border}
        .input
          color ${inputAtoms.const.color}
          font ${inputSizeAtoms.font}
          padding ${inputSizeAtoms.padding}

          &::placeholder
            color ${inputStateAtoms.idle.placeholderColor}

          &:disabled
            &::placeholder
              color ${inputStateAtoms.disabled.placeholderColor}

          @media screen and (-webkit-min-device-pixel-ratio: 0)
            // disable page zoom with input:focus on iOS devices
            font-size 16px
        .error
          &.borderer
          .input:focus ~ &.borderer
          .input:hover ~ &.borderer
            background ${inputAtoms.basic.error.background}
            border ${inputAtoms.basic.error.border}
        .focused
          &.borderer
            background ${inputAtoms.basic.focused.background}
            border ${inputAtoms.basic.focused.border}
        .icons
          padding ${noIconsPadding ? 0 : inputSizeAtoms.padding}
          padding-left 0
      `}</style>
      <input
        className={cx(inputClassName, 'input', styles.input)}
        ref={innerRef}
        disabled={disabled}
        {...inputProps}
      />
      <div className={cx('borderer', styles.borderer, error && 'error')} />
      {isNotEmptyReactElementContent(children) &&
        <div className={cx('icons', styles.icons, iconClassName)}>
          {children}
        </div>
      }
    </label>
  );
}

Input.propTypes = {
  /** Применяет стили невалидного поля */
  error: PropTypes.bool,

  /** Поле, недоступное для редактирования. */
  disabled: PropTypes.bool,

  /** Функция, получающая в качестве аргумента ref поля ввода */
  innerRef: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),

  /** Размер */
  size: sizeTypes,

  /** Отключает отступы контейнера иконок. */
  noIconsPadding: PropTypes.bool,

  /**
   * Радиус границы, с указанием единиц измерения.
   * Имеет приоритет над указанным в теме.
   */
  radius: PropTypes.string,

  /**
   * Выстота инпута, с указанием единиц измерения.
   * Имеет приоритет над указанным в теме.
   */
  height: PropTypes.string,

  /** @ignore */
  theme: themePropTypes(`
    {
    animations: {
      hover,
    },
    controls: {
      input: {
        const: {
          radius,
          color,
          sizes: {
            small: ${sizeContract},
            medium: ${sizeContract},
            large: ${sizeContract},
          },
        },
        basic: ${typeContract},
      },
    },
  `),

  /** Атрибут `type` */
  type: PropTypes.oneOf(['text', 'tel', 'number', 'email']),

  /** @ignore */
  className: PropTypes.string,

  /** Имя класса для кастомизации иконки */
  iconClassName: PropTypes.string,

  /** Имя класса для кастомизации инпута */
  inputClassName: PropTypes.string,
};

Input.defaultProps = {
  error: false,
  size: 'medium',
  type: 'text',
};

const InputWithHOCs = withTheme(Input);
InputWithHOCs.displayName = 'Input';

export { Input as StorybookComponent };
export default InputWithHOCs;
